import { useState, type CSSProperties, type FormEvent } from 'react';

import { Modal } from '../ui';
import { api } from '../../lib/api';
import type { BaselineView, FirewallOverview, SystemView } from './types';
import { errorText } from './types';

const GROUP_TONES = ['e-product', 'e-container', 'e-llm', 'e-endpoint', 'e-client'] as const;

type Target = { scope: 'SUBNET' } | { scope: 'GROUP'; id: string; name: string } | { scope: 'SERVER'; id: string; name: string };

function targetBody(t: Target) {
  if (t.scope === 'SUBNET') return { scope: 'SUBNET', targetId: null };
  return { scope: t.scope, targetId: t.id };
}

function isOn(item: BaselineView, t: Target): boolean {
  if (t.scope === 'SUBNET') return item.states.subnet;
  if (t.scope === 'GROUP') return item.states.groups[t.id] === true;
  return item.states.systems[t.id] === true;
}

function cellKey(item: BaselineView, t: Target): string {
  return `${item.key}:${t.scope}:${'id' in t ? t.id : ''}`;
}

/**
 * Suggested controls, off by default. Each cell is one choice: this control on this
 * target. Turning one on adds a draft rule to the list above; nothing reaches the
 * host until someone applies the change.
 */
export function BaselineMatrix({
  overview,
  canManage,
  onChanged,
}: {
  overview: FirewallOverview;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busyCell, setBusyCell] = useState<string | null>(null);
  const [asking, setAsking] = useState<{ item: BaselineView; target: Target } | null>(null);

  const groups = overview.groups;
  const systems = overview.systems;

  const send = async (item: BaselineView, target: Target, enabled: boolean, sources?: string[]) => {
    setBusyCell(cellKey(item, target));
    setError(null);
    try {
      await api(`/connectiq/firewall/baseline/${item.key}`, {
        method: 'POST',
        body: JSON.stringify({ ...targetBody(target), enabled, ...(sources ? { sources } : {}) }),
      });
      onChanged();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusyCell(null);
    }
  };

  const toggle = (item: BaselineView, target: Target) => {
    const next = !isOn(item, target);
    if (next && item.sourcesMode === 'required') {
      setAsking({ item, target });
      return;
    }
    void send(item, target, next);
  };

  const renderSwitch = (item: BaselineView, target: Target, label: string) => {
    const on = isOn(item, target);
    return (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        className={on ? 'ciq-switch small on' : 'ciq-switch small'}
        disabled={!canManage || busyCell === cellKey(item, target)}
        onClick={() => toggle(item, target)}
      >
        <span className="ciq-switch-knob" aria-hidden="true" />
      </button>
    );
  };

  /**
   * One dropdown per control: tick one or more systems. Each tick is its own
   * change, so the list always shows what is actually stored.
   */
  const renderSystemPicker = (item: BaselineView) => {
    const allowed = systems.filter((s) => item.allowedSystemIds.includes(s.id));
    if (allowed.length === 0) return <span className="ciq-muted" aria-label="Not applicable">—</span>;
    const chosen = allowed.filter((s) => item.states.systems[s.id] === true);
    const summary = chosen.length === 0 ? 'None selected' : chosen.length === 1 ? chosen[0].name : `${chosen.length} systems`;
    const busy = busyCell?.startsWith(`${item.key}:SERVER:`) ?? false;
    return (
      <details className="ciq-picker">
        <summary aria-label={`Systems for ${item.title}: ${summary}`}>
          <span>{summary}</span>
          <span aria-hidden="true" className="ciq-picker-caret">▾</span>
        </summary>
        <div className="ciq-picker-menu" role="group" aria-label={`Choose systems for ${item.title}`}>
          {allowed.map((s: SystemView) => {
            const on = item.states.systems[s.id] === true;
            return (
              <label key={s.id} className="ciq-picker-option">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!canManage || busy}
                  onChange={() => toggle(item, { scope: 'SERVER', id: s.id, name: s.name })}
                />
                <span>
                  <strong>{s.name}</strong>
                  <span className="mono ciq-muted small"> {s.address ?? ''}</span>
                </span>
              </label>
            );
          })}
        </div>
      </details>
    );
  };

  return (
    <section className="ciq-baseline" aria-label="Suggested baseline">
      <header className="ciq-rules-head">
        <div>
          <h3>Suggested baseline</h3>
          <p className="ciq-muted small">
            Off by default. Turning a control on adds a draft rule to the list above, and nothing reaches the host until you apply it.
            Mappings are suggestions for reviewers; confirm which frameworks apply with your control owner.
          </p>
        </div>
      </header>

      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="table-scroll">
        <table className="data-table ciq-table ciq-matrix">
          <thead>
            <tr className="ciq-matrix-groups">
              <th rowSpan={2}>Control</th>
              <th rowSpan={2}>Frameworks</th>
              <th rowSpan={2} className="ciq-col-center">Every system</th>
              {groups.length > 0 && (
                <th colSpan={groups.length} className="ciq-col-band" style={{ '--band': 'var(--accent)' } as CSSProperties}>
                  Enable for group
                </th>
              )}
              <th rowSpan={2} className="ciq-col-band" style={{ '--band': 'var(--e-server)' } as CSSProperties}>
                Enable on systems
                <div className="ciq-muted small">Pick one or more</div>
              </th>
            </tr>
            <tr>
              {groups.map((g, i) => (
                <th key={g.id} className="ciq-col-target" style={{ '--band': `var(--${GROUP_TONES[i % GROUP_TONES.length]})` } as CSSProperties}>
                  <div className="ciq-col-name">{g.name}</div>
                  <div className="ciq-muted small">{g.memberIds.length} system{g.memberIds.length === 1 ? '' : 's'}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {overview.baseline.map((item) => (
              <tr key={item.key}>
                <td className="ciq-matrix-control">
                  <div className="ciq-matrix-title">{item.title}</div>
                  <div className="ciq-muted small">{item.plain}</div>
                  <div className="small">
                    <span className={item.action === 'ALLOW' ? 'ciq-verb allow' : 'ciq-verb block'}>{item.action === 'ALLOW' ? 'Allow' : 'Block'}</span>{' '}
                    <span className="ciq-chip mono">{item.protocol.toUpperCase()} {item.port}</span>
                    {item.sourcesMode === 'required' && <span className="ciq-muted small"> · you choose the sources</span>}
                  </div>
                </td>
                <td>
                  <details className="ciq-frameworks">
                    <summary>{item.frameworks.length} mapping{item.frameworks.length === 1 ? '' : 's'}</summary>
                    <ul>
                      {item.frameworks.map((f) => (
                        <li key={f} className="small">{f}</li>
                      ))}
                    </ul>
                  </details>
                </td>
                <td className="ciq-col-center">
                  {item.allowedOnSubnet
                    ? renderSwitch(item, { scope: 'SUBNET' }, `${item.title}: enable for every system`)
                    : <span className="ciq-muted" aria-label="Not applicable">—</span>}
                </td>
                {groups.map((g) => (
                  <td key={g.id} className="ciq-col-center">
                    {item.allowedOnGroups
                      ? renderSwitch(item, { scope: 'GROUP', id: g.id, name: g.name }, `${item.title}: enable for group ${g.name}`)
                      : <span className="ciq-muted" aria-label="Not applicable">—</span>}
                  </td>
                ))}
                <td className="ciq-col-center">{renderSystemPicker(item)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SourcesDialog
        asking={asking}
        onClose={() => setAsking(null)}
        onConfirm={(sources) => {
          if (!asking) return;
          const { item, target } = asking;
          setAsking(null);
          void send(item, target, true, sources);
        }}
      />
    </section>
  );
}

function SourcesDialog({
  asking,
  onClose,
  onConfirm,
}: {
  asking: { item: BaselineView; target: Target } | null;
  onClose: () => void;
  onConfirm: (sources: string[]) => void;
}) {
  const [text, setText] = useState('');
  const sources = text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const targetLabel =
    asking?.target.scope === 'SUBNET' ? 'every system' : asking?.target.scope === 'GROUP' ? `group ${asking.target.name}` : asking?.target.scope === 'SERVER' ? asking.target.name : '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (sources.length) onConfirm(sources);
  };

  return (
    <Modal open={asking !== null} onClose={onClose} title={asking ? `Enable ${asking.item.title}` : ''}>
      <form className="ciq-form" onSubmit={submit}>
        <p>
          This opens <strong>{asking?.item.port}/{asking?.item.protocol}</strong> on {targetLabel} to the source ranges below. Enter only the networks
          that genuinely need it, such as your admin workstations. It will expire in 365 days and be reviewed at 180.
        </p>
        <label className="ciq-field">
          <span>Source ranges (CIDR, separated by commas or spaces)</span>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="203.0.113.0/24" autoFocus autoComplete="off" spellCheck={false} />
        </label>
        {sources.length > 0 && (
          <div className="ciq-chips">
            {sources.map((s) => (
              <span key={s} className="ciq-chip mono">{s}</span>
            ))}
          </div>
        )}
        <div className="ciq-actions">
          <button type="button" className="ghost" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={sources.length === 0}>Enable as a draft</button>
        </div>
      </form>
    </Modal>
  );
}
